/*
  # Booking success page with park confirmation messaging

  1. Changes
    - Display booking confirmation details
    - Send confirmation emails to customer and admin
    - Added useRef to prevent duplicate email sending on re-renders
    - Shows park confirmation waiting message for tours requiring park approval
    - Display participant details when applicable
*/

import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Calendar, MapPin, Users, Mail, Phone, ArrowLeft, AlertCircle, Clock, FileText } from 'lucide-react';
import { Tour } from '../../types/tours';
import { BookingParticipant } from '../../types/bookings';
import { formatDate, formatTime } from '../../utils/dateUtils';

interface BookingSuccessStepProps {
  tour: Tour;
  data: {
    date: Date | null;
    groupType: 'group' | 'reduced' | 'private' | null;
    persons: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    totalPrice: number;
    paymentMethod?: 'cash' | 'paypal';
    bookingCode?: string;
    bootSizes?: string[];
    participants?: BookingParticipant[];
  };
}

const BookingSuccessStep: React.FC<BookingSuccessStepProps> = ({ tour, data }) => {
  const [emailsSent, setEmailsSent] = React.useState(false);
  const [emailError, setEmailError] = React.useState<string | null>(null);
  const emailsSentRef = React.useRef(false);

  // Calculate PayPal commission based on price ranges
  const calculatePayPalCommission = (amount: number): number => {
    if (amount >= 1 && amount <= 50.99) return 4;
    if (amount >= 51 && amount <= 100.99) return 6;
    if (amount >= 101 && amount <= 150.99) return 7;
    if (amount >= 151 && amount <= 200.99) return 8;
    if (amount >= 201 && amount <= 250.99) return 9;
    if (amount >= 251 && amount <= 300.99) return 10;
    if (amount >= 301 && amount <= 350.99) return 11;
    if (amount >= 351 && amount <= 400.99) return 12;
    if (amount >= 401 && amount <= 500.99) return 14;
    if (amount >= 501 && amount <= 1000.99) return 24;
    if (amount >= 1001 && amount <= 1500.99) return 34;
    return 34; // For amounts above $1500.99
  };

  const paymentMethod = data.paymentMethod || 'cash';
  const paypalCommission = calculatePayPalCommission(data.totalPrice);
  const finalTotal = paymentMethod === 'paypal' ? data.totalPrice + paypalCommission : data.totalPrice;

  // Send emails when component mounts
  React.useEffect(() => {
    const sendEmails = async () => {
      // Use ref to prevent any re-renders from triggering duplicate emails
      if (emailsSentRef.current) return;
      emailsSentRef.current = true;

      try {
        const { sendBookingEmails } = await import('../../lib/emailjs');
        
        // Add boot sizes if available
        let bootSizesText = 'No rental boots requested';
        if (tour.has_rental_boots && data.bootSizes && data.bootSizes.length > 0) {
          bootSizesText = data.bootSizes.map((size, index) => 
            `Participant ${index + 1}: Size ${size} (US ${
              size === '35' ? '5' :
              size === '36' ? '6' :
              size === '37' ? '6.5' :
              size === '38' ? '7' :
              size === '39' ? '7.5' :
              size === '40' ? '8' :
              size === '41' ? '8.5' :
              size === '42' ? '9' :
              size === '43' ? '9.5' :
              size === '44' ? '10' :
              size === '45' ? '11' :
              size === '46' ? '12' :
              size === '47' ? '13' :
              size === '48' ? '14' : size
            })`
          ).join(', ');
        }

        await sendBookingEmails({
          tourName: tour.name,
          bookingDate: data.date?.toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          }) || '',
          groupType: data.groupType || '',
          persons: data.persons,
          firstName: data.firstName.trim(),
          lastName: data.lastName.trim(),
          email: data.email.trim(),
          phone: data.phone.trim(),
          totalPrice: finalTotal,
          paymentMethod: paymentMethod,
          bookingCode: data.bookingCode || '',
          bootSizes: bootSizesText
        });

        setEmailsSent(true);
      } catch (error) {
        console.error('Error sending emails:', error);
        setEmailError('Failed to send confirmation emails. Your booking is still confirmed.');
      }
    };

    sendEmails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  return (
    <div className="space-y-8">
      {/* Success Message */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          {tour.requires_park_confirmation ? (
            <Clock className="w-16 h-16 text-amber-500" />
          ) : (
            <CheckCircle className="w-16 h-16 text-[#E85C0D]" />
          )}
        </div>
        <h2 className="text-2xl font-bold text-[#2A382A] mb-2">
          {tour.requires_park_confirmation
            ? 'Reservation Request Submitted!'
            : 'Booking Request Sent!'}
        </h2>
        <p className="text-[#2A382A]/70">
          {tour.requires_park_confirmation
            ? 'Your reservation request has been submitted. We will confirm availability with Corcovado National Park and notify you within 24 hours via email.'
            : 'Your booking request has been successfully sent. We aim to confirm your request within 1 hour, but surely within 12 hours. Make sure to save your booking code and details.'}
        </p>
      </div>

      {/* Booking Code Box */}
      <div className="bg-emerald-50 rounded-xl p-6 border-2 border-emerald-500 text-center">
        <h3 className="text-lg font-semibold text-emerald-700 mb-2">Booking Code</h3>
        <p className="text-2xl font-bold text-emerald-600 font-mono tracking-wider">
          {data.bookingCode || `SF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`}
        </p>
      </div>

      {/* Email Error */}
      {emailError && (
        <div className="bg-yellow-50 rounded-xl p-6 border-2 border-yellow-500">
          <div className="flex items-center gap-2 text-yellow-700">
            <AlertCircle className="w-5 h-5" />
            <p className="text-sm font-medium">{emailError}</p>
          </div>
        </div>
      )}

      {/* Booking Details */}
      <div className="bg-[#F2F3F4] rounded-xl p-6 space-y-6">
        <h2 className="font-semibold text-[#2A382A]">Booking Summary</h2>

        <div className="space-y-4">
          {/* Tour Info */}
          <div className="pb-4 border-b border-[#2A382A]/10">
            <h3 className="font-medium text-[#2A382A] mb-2">{tour.name}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-[#2A382A]/40" />
                <div>
                  <p className="text-sm text-[#2A382A]">
                    {data.date && formatDate(data.date, {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="text-xs text-[#2A382A]/70">
                    Starting at {formatTime(tour.departure).americanTime} ({formatTime(tour.departure).europeanTime})
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#2A382A]/40" />
                <div>
                  <p className="text-sm text-[#2A382A]">{tour.meeting_point}</p>
                  <p className="text-xs text-[#2A382A]/70">VG5H+J7H, Provincia de Puntarenas, Sierpe, Costa Rica</p>
                  <div className="flex flex-col gap-1 mt-2">
                    <p className="text-xs text-[#953B0E] font-medium">Please arrive 15 minutes before the starting time</p>
                    <a 
                      href={tour.meeting_point_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-[#953B0E] hover:underline"
                    >
                      View on Google Maps
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Group Details */}
          <div className="flex justify-between items-center pb-4 border-b border-[#2A382A]/10">
            <div>
              <h4 className="font-medium text-[#2A382A]">Group Details</h4>
              <p className="text-sm text-[#2A382A]/70 mb-1">
                {data.groupType === 'private' 
                  ? `Private tour with ${data.persons} ${data.persons === 1 ? 'person' : 'persons'}`
                  : `${data.groupType === 'reduced' ? 'Reduced group' : 'Group'} tour with ${data.persons} ${data.persons === 1 ? 'person' : 'persons'}`
                }
              </p>
              <div className="space-y-2">
                <div className="inline-flex items-center bg-[#953B0E]/10 px-3 py-1 rounded-full">
                  <span className="text-[#953B0E] font-semibold">
                    {paymentMethod === 'cash' ? 'Cash Payment' : 'PayPal Payment'}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#2A382A]/70">Tour Price:</span>
                    <span className="text-[#2A382A]">${data.totalPrice.toFixed(2)}</span>
                  </div>
                  {paymentMethod === 'paypal' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-[#2A382A]/70">PayPal Commission:</span>
                      <span className="text-[#2A382A]">${paypalCommission.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-semibold border-t border-[#2A382A]/10 pt-1">
                    <span className="text-[#2A382A]">Total Amount:</span>
                    <span className="text-[#2A382A]">${finalTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Boot Sizes (if applicable) */}
          {tour.has_rental_boots && data.bootSizes.length > 0 && (
            <div className="pb-4 border-b border-[#2A382A]/10">
              <h4 className="font-medium text-[#2A382A] mb-2">Rubber Boot Sizes</h4>
              <div className="text-sm text-[#2A382A]/70 space-y-1">
                {data.bootSizes.map((size, index) => (
                  <p key={index}>
                    Participant {index + 1}: Size {size} (US {
                      size === '35' ? '5' :
                      size === '36' ? '6' :
                      size === '37' ? '6.5' :
                      size === '38' ? '7' :
                      size === '39' ? '7.5' :
                      size === '40' ? '8' :
                      size === '41' ? '8.5' :
                      size === '42' ? '9' :
                      size === '43' ? '9.5' :
                      size === '44' ? '10' :
                      size === '45' ? '11' :
                      size === '46' ? '12' :
                      size === '47' ? '13' :
                      size === '48' ? '14' : size
                    })
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* Contact Info */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-[#2A382A]/40" />
              <p className="text-sm text-[#2A382A]">{data.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-[#2A382A]/40" />
              <p className="text-sm text-[#2A382A]">{data.phone}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-[#F2F3F4] rounded-xl p-6">
        <h3 className="font-semibold text-[#2A382A] mb-2">Payment Information</h3>
        <p className="text-sm text-[#2A382A]/70 mb-4">
          Payment will be collected upon arrival at the meeting point using your selected method: {paymentMethod === 'cash' ? 'Cash' : 'PayPal transfer'}.
        </p>
        <div className="flex items-center gap-2 text-[#953B0E]">
          <AlertCircle className="w-5 h-5" />
          <p className="text-sm font-medium">
            Please have ${finalTotal.toFixed(2)} ready before the tour starts
          </p>
        </div>
      </div>

      {/* Participant Details (for park confirmation tours) */}
      {tour.requires_participant_details && data.participants && data.participants.length > 0 && (
        <div className="bg-white rounded-xl border border-[#2A382A]/10 p-6">
          <h3 className="font-semibold text-[#2A382A] mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Registered Participants
          </h3>
          <div className="space-y-2">
            {data.participants.map((p, index) => (
              <div key={index} className="flex items-center gap-3 text-sm text-[#2A382A]/80 bg-[#F2F3F4] rounded-lg px-4 py-2">
                <span className="font-medium">{index + 1}.</span>
                <span>{p.full_name}</span>
                <span className="text-[#2A382A]/50">|</span>
                <span>Passport: {p.passport_number}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Park Confirmation Notice */}
      {tour.requires_park_confirmation && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">What happens next?</p>
            <p>We are now contacting Corcovado National Park to confirm your spot. You will receive an email confirmation within 24 hours. If the park cannot accommodate your group on the selected date, we will contact you to arrange an alternative.</p>
          </div>
        </div>
      )}

      {/* Back to Home */}
      <div className="flex justify-center">
        <Link
          to="/"
          className="flex items-center gap-2 px-6 py-2 bg-[#953B0E] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
    </div>
  );
};

export default BookingSuccessStep;